import { measureFrame, VOICE_LEVEL, VoiceLevelFrame } from '../../utils/voice-level';

/**
 * Taps the reading's own microphone graph with an AnalyserNode and reports a
 * level every 100 ms. It opens no stream of its own and sits beside the
 * worklet, so the audio that goes to the server is untouched.
 */
export class VoiceLevelMonitor {
  private analyser: AnalyserNode | null = null;
  private source: AudioNode | null = null;
  private buffer: Float32Array | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly onFrame: (frame: VoiceLevelFrame) => void) {}

  get isRunning(): boolean {
    return this.timer !== null;
  }

  start(audioContext: AudioContext, source: AudioNode): void {
    if (this.isRunning && this.source === source && this.analyser?.context === audioContext) {
      // The source may have been disconnected from everything in between;
      // connecting the same pair again is harmless.
      source.connect(this.analyser);
      return;
    }
    this.stop();

    const analyser = audioContext.createAnalyser();
    // 1024 samples at 16 kHz is 64 ms: enough for a level, cheap to read.
    analyser.fftSize = 1024;
    source.connect(analyser);

    this.analyser = analyser;
    this.source = source;
    this.buffer = new Float32Array(analyser.fftSize);
    this.timer = setInterval(() => {
      if (!this.analyser || !this.buffer) return;
      this.analyser.getFloatTimeDomainData(this.buffer as Float32Array<ArrayBuffer>);
      this.onFrame(measureFrame(this.buffer));
    }, VOICE_LEVEL.SAMPLE_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.source && this.analyser) {
      try {
        this.source.disconnect(this.analyser);
      } catch {
        // Already disconnected (the controller disconnects the whole input on pause).
      }
    }
    this.analyser = null;
    this.source = null;
    this.buffer = null;
  }
}
